import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, FilterIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { ConsentState, ConsentType } from '../../constants/uaeStandards';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
interface SearchFilterBarProps {
  onSearch: (query: string) => void;
  onFilterChange: (filters: FilterOptions) => void;
  availableBanks?: string[];
  context: 'TPP' | 'LFI';
}
export interface FilterOptions {
  bankName?: string;
  accountNumber?: string;
  consentType?: ConsentType;
  consentState?: ConsentState;
  dateRange?: {
    from: string;
    to: string;
  };
}
export function SearchFilterBar({
  onSearch,
  onFilterChange,
  availableBanks = [],
  context
}: SearchFilterBarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterOptions>({});
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    onSearch(value);
  };
  const handleFilterChange = (key: keyof FilterOptions, value: any) => {
    const newFilters = {
      ...filters,
      [key]: value
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };
  const clearFilters = () => {
    setFilters({});
    onFilterChange({});
  };
  const activeFilterCount = Object.values(filters).filter(v => v !== undefined && v !== '').length;
  const consentStates: ConsentState[] = context === 'LFI' ? ['Awaiting', 'Authorized', 'Rejected', 'Canceled', 'Consumed', 'Suspended', 'Expired'] : ['Authorized', 'Paused', 'Canceled', 'Expired'];
  return <div className="space-y-3" data-id="element-1674">
      {/* Search Bar - Responsive */}
      <div className="flex flex-col sm:flex-row gap-2" data-id="element-1675">
        <div className="flex-1 relative" data-id="element-1676">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 pointer-events-none" style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-1677" />
          <input type="text" placeholder={context === 'TPP' ? 'Search by bank name...' : 'Search by TPP name...'} value={searchQuery} onChange={e => handleSearchChange(e.target.value)} className="w-full pl-10 pr-4 py-3 border-2 focus:outline-none focus:ring-2 focus:border-transparent text-sm" style={{
          borderColor: OPENFINANCE_THEME.colors.border,
          borderRadius: OPENFINANCE_THEME.radius.md,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-1678" />
        </div>

        <button onClick={() => setShowFilters(!showFilters)} className="w-full sm:w-auto px-4 py-3 border-2 font-medium transition-all flex items-center justify-center gap-2 whitespace-nowrap" style={{
        borderColor: showFilters ? OPENFINANCE_THEME.colors.primary500 : OPENFINANCE_THEME.colors.border,
        backgroundColor: showFilters ? OPENFINANCE_THEME.colors.primary50 : 'white',
        color: showFilters ? OPENFINANCE_THEME.colors.primary600 : OPENFINANCE_THEME.colors.text,
        borderRadius: OPENFINANCE_THEME.radius.md
      }} data-id="element-1679">
          <FilterIcon className="w-5 h-5" data-id="element-1680" />
          <span data-id="element-1681">Filters</span>
          {activeFilterCount > 0 && <span className="px-2 py-0.5 text-xs font-bold rounded-full" style={{
          backgroundColor: OPENFINANCE_THEME.colors.accent500,
          color: 'white'
        }} data-id="element-1682">
              {activeFilterCount}
            </span>}
        </button>
      </div>

      {/* Filter Panel - Responsive grid */}
      <AnimatePresence data-id="element-1683">
        {showFilters && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-1684">
            <div className="p-4 border-2 space-y-4" style={{
          borderColor: OPENFINANCE_THEME.colors.border,
          borderRadius: OPENFINANCE_THEME.radius.lg,
          backgroundColor: OPENFINANCE_THEME.colors.cardBg
        }} data-id="element-1685">
              <div className="flex items-center justify-between" data-id="element-1686">
                <div className="font-semibold text-sm" style={{
              color: OPENFINANCE_THEME.colors.text,
              fontFamily: OPENFINANCE_THEME.fonts.body
            }} data-id="element-1687">
                  Filter Consents
                </div>
                {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs font-medium flex items-center gap-1 transition-colors" style={{
              color: OPENFINANCE_THEME.colors.primary500
            }} data-id="element-1688">
                    <XIcon className="w-4 h-4" data-id="element-1689" />
                    <span className="hidden sm:inline" data-id="element-1690">Clear all</span>
                    <span className="sm:hidden" data-id="element-1691">Clear</span>
                  </button>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1692">
                {/* Bank/TPP Name Filter */}
                {availableBanks.length > 0 && <div data-id="element-1693">
                    <label className="block text-xs font-medium mb-2" style={{
                color: OPENFINANCE_THEME.colors.textLight
              }} data-id="element-1694">
                      {context === 'TPP' ? 'Bank' : 'TPP'}
                    </label>
                    <select value={filters.bankName || ''} onChange={e => handleFilterChange('bankName', e.target.value || undefined)} className="w-full px-3 py-2 border-2 focus:outline-none focus:ring-2 focus:border-transparent text-sm" style={{
                borderColor: OPENFINANCE_THEME.colors.border,
                borderRadius: OPENFINANCE_THEME.radius.md
              }} data-id="element-1695">
                      <option value="" data-id="element-1696">All</option>
                      {availableBanks.map(bank => <option key={bank} value={bank} data-id="element-1697">
                          {bank}
                        </option>)}
                    </select>
                  </div>}

                {/* Consent Type Filter */}
                <div data-id="element-1698">
                  <label className="block text-xs font-medium mb-2" style={{
                color: OPENFINANCE_THEME.colors.textLight
              }} data-id="element-1699">
                    Type
                  </label>
                  <select value={filters.consentType || ''} onChange={e => handleFilterChange('consentType', e.target.value || undefined)} className="w-full px-3 py-2 border-2 focus:outline-none focus:ring-2 focus:border-transparent text-sm" style={{
                borderColor: OPENFINANCE_THEME.colors.border,
                borderRadius: OPENFINANCE_THEME.radius.md
              }} data-id="element-1700">
                    <option value="" data-id="element-1701">All Types</option>
                    <option value="DataSharing" data-id="element-1702">Data Sharing</option>
                    <option value="ServiceInitiation" data-id="element-1703">
                      Service Initiation
                    </option>
                  </select>
                </div>

                {/* Consent State Filter */}
                <div data-id="element-1704">
                  <label className="block text-xs font-medium mb-2" style={{
                color: OPENFINANCE_THEME.colors.textLight
              }} data-id="element-1705">
                    Status
                  </label>
                  <select value={filters.consentState || ''} onChange={e => handleFilterChange('consentState', e.target.value || undefined)} className="w-full px-3 py-2 border-2 focus:outline-none focus:ring-2 focus:border-transparent text-sm" style={{
                borderColor: OPENFINANCE_THEME.colors.border,
                borderRadius: OPENFINANCE_THEME.radius.md
              }} data-id="element-1706">
                    <option value="" data-id="element-1707">All Statuses</option>
                    {consentStates.map(state => <option key={state} value={state} data-id="element-1708">
                        {state}
                      </option>)}
                  </select>
                </div>

                {/* Account Number Filter */}
                <div data-id="element-1709">
                  <label className="block text-xs font-medium mb-2" style={{
                color: OPENFINANCE_THEME.colors.textLight
              }} data-id="element-1710">
                    Account (Last 4)
                  </label>
                  <input type="text" placeholder="e.g., 1234" maxLength={4} value={filters.accountNumber || ''} onChange={e => handleFilterChange('accountNumber', e.target.value || undefined)} className="w-full px-3 py-2 border-2 focus:outline-none focus:ring-2 focus:border-transparent text-sm font-mono" style={{
                borderColor: OPENFINANCE_THEME.colors.border,
                borderRadius: OPENFINANCE_THEME.radius.md
              }} data-id="element-1711" />
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}